/**
 * Guard for posts that carry no media at all.
 *
 * Removing a post's image (ImageMediaComponent's Remove action) detaches it without
 * deleting anything from S3, which means a post can now legitimately reach a zero-media
 * state — something the UI could not produce before. Every platform this app publishes to
 * needs at least one media item, so approve/publish must refuse that state up front rather
 * than let it fail deep inside the publish pipeline.
 *
 * Instagram has its own stricter rules (Reels need a video specifically); those live in
 * instagramPublishMediaValidation.ts and run in addition to this check.
 */

export const POST_MEDIA_REQUIRED_MESSAGE =
  "Add an image or video before approving or publishing this post.";

function hasUrl(value?: string | null): boolean {
  return Boolean(String(value ?? "").trim());
}

/**
 * Platforms whose APIs reject a post with no media outright, so publishing without it can
 * only fail: Instagram (all surfaces), any Reels surface, and YouTube — all are media-only
 * by definition. For everything else (Facebook, LinkedIn, GMB) a text-only post is a valid
 * request, so a missing image is a likely mistake rather than an impossibility, and the user
 * is asked to confirm instead of being blocked.
 */
export function platformRequiresMedia(platform?: string): boolean {
  const p = String(platform ?? "").toUpperCase();
  return p.includes("INSTAGRAM") || p.includes("REELS") || p.includes("YOUTUBE");
}

export function postHasAnyMedia(post: {
  image_url?: string;
  video_url?: string;
  carousel_media?: Array<{ url?: string }>;
}): boolean {
  return (
    hasUrl(post.image_url) ||
    hasUrl(post.video_url) ||
    Boolean(post.carousel_media?.some((slide) => hasUrl(slide.url)))
  );
}
